import { useState, useCallback, useRef, useEffect } from 'react';
import { encryptFile, decryptFile, base64ToBuffer, bufferToBase64, encryptBuffer, decryptBuffer, leggiFileIntero } from '../utils/crypto';
import { blocchiDisponibili, sessioniAperte, caricaABlocchi, scaricaABlocchi, spazzaFileDiLavoro } from '../utils/caricamentoBlocchi';
import { generaAnteprima, puoAvereAnteprima } from '../utils/anteprima';
import { DIMENSIONE_BLOCCO } from '../utils/cifraturaBlocchi';
import { apiSetAnteprima, apiTogliAnteprima } from '../utils/api';
import { apiListFiles, apiListRecent, apiListAllFiles, apiListPreferiti, apiSetPreferito, apiListFolders, apiListAllFolders, apiUploadFile, apiReplaceFile, apiDownloadFile, marcaVersione, apiDeleteFile,
         apiCreateFolder, apiDeleteFolder, apiRenameFolder, apiMoveFolder, apiQuota, apiMoveFile, apiSetDidascalia } from '../utils/api';
import { useAuth } from './useAuth';

// Plain names, no emoji prefix (2026-08-28) — the folder's own colored icon
// (see components/FolderIcon.jsx's detectType) already conveys the type
// visually; detectType falls back to matching the word itself
// (/\bimages?\b/i etc.) when there's no emoji in the name, so these still
// get their distinct badge without it.


// UN SOLO AVANZAMENTO PER DUE FASI (2026-09-12).
//
// Le due fasi stanno nella stessa barra ma non pesano uguale, e i pesi non
// sono inventati: misurato su 250 MB, la cifratura e' circa 1 secondo e
// l'invio circa 140. Dare meta' barra alla cifratura l'avrebbe fatta volare
// al 50% in un istante e poi strisciare, che e' lo stesso inganno di prima
// al contrario. La cifratura prende il primo 15%, l'invio il resto: dentro
// ciascun tratto la percentuale avanza per davvero.
//
// La stima del tempo si calcola SOLO sull'invio e solo dopo il primo 2%:
// prima di allora la velocita' misurata e' quella del riempimento dei buffer,
// non della rete, e darebbe numeri ridicoli. Quando non e' calcolabile, si
// restituisce null e chi disegna non scrive niente — mai un "meno di un
// minuto" buttato li'.
const PESO_CIFRATURA = 15;

function avanzamentoUnico(setStato, setPercento) {
  let ultimoDisegno = 0;
  let inizioInvio = 0;
  // L'indicatore si aggiorna al massimo dieci volte al secondo: onprogress
  // puo' scattare centinaia di volte e ridisegnare a ogni scatto non si vede
  // ma si paga. `forza` salta il freno sui passaggi che contano.
  const disegna = (stato, percento, forza) => {
    const ora = Date.now();
    if (!forza && ora - ultimoDisegno < 100) return;
    ultimoDisegno = ora;
    setStato(stato);
    setPercento(percento);
  };
  return {
    cifratura(frazione) {
      const indeterminato = frazione == null;
      const percento = indeterminato ? PESO_CIFRATURA : Math.round(frazione * PESO_CIFRATURA);
      disegna({ fase: 'cifratura', frazione: indeterminato ? null : frazione, inviati: null, totale: null, etaSec: null },
        percento, indeterminato || frazione === 0);
    },
    invio(inviati, totale) {
      if (!inizioInvio) inizioInvio = Date.now();
      const frazione = totale ? inviati / totale : null;
      const percento = frazione == null ? PESO_CIFRATURA : Math.round(PESO_CIFRATURA + frazione * (100 - PESO_CIFRATURA));
      let etaSec = null;
      if (frazione != null && frazione > 0.02) {
        const passati = (Date.now() - inizioInvio) / 1000;
        if (passati > 1) etaSec = Math.max(1, Math.round(passati / frazione - passati));
      }
      disegna({ fase: 'invio', frazione, inviati, totale, etaSec }, percento, inviati === 0);
    },
    // I BLOCCHI NON HANNO DUE FASI. Si cifra e si spedisce lo stesso pezzo, uno
    // dopo l'altro: la percentuale e' i blocchi finiti sul totale, e va da 0 a
    // 100 senza il salto del 15% che serviva quando la cifratura veniva prima.
    blocchi(fatti, totale) {
      if (!inizioInvio) inizioInvio = Date.now();
      const frazione = totale ? fatti / totale : null;
      let etaSec = null;
      if (frazione != null && frazione > 0.02) {
        const passati = (Date.now() - inizioInvio) / 1000;
        if (passati > 1) etaSec = Math.max(1, Math.round(passati / frazione - passati));
      }
      disegna({ fase: 'blocchi', frazione, inviati: fatti, totale, etaSec },
        frazione == null ? 0 : Math.round(frazione * 100), fatti === 0 || fatti === totale);
    },
    fine() {
      disegna({ fase: 'invio', frazione: 1, inviati: null, totale: null, etaSec: null }, 100, true);
    },
  };
}

// Le didascalie gia' decifrate, per questa sessione e solo in memoria.
const memoriaDidascalie = new Map();
export function svuotaMemoriaDidascalie() { memoriaDidascalie.clear(); }

export function useFiles() {
  const { encKey } = useAuth();
  const [files, setFiles]         = useState([]);
  const [folders, setFolders]       = useState([]);
  const [allFolders, setAllFolders]  = useState([]); // flat list for sidebar tree
  const [quota, setQuota]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading]       = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  // LO STATO RICCO DELL'INDICATORE (2026-09-12). uploadProgress da solo non
  // bastava: era una successione di quattro salti fissi (10, 55, 70, 100) e il
  // 70 veniva messo PRIMA della chiamata di rete, quindi restava li' per tutta
  // la durata dell'invio — su 250 MB, misurato, circa 140 secondi su 141.
  //   fase          'cifratura' | 'invio'
  //   frazione      0..1 dentro la fase, oppure null quando non c'e' niente di
  //                 vero da misurare (la crypto.subtle.encrypt e' atomica):
  //                 in quel caso si disegna un'animazione indeterminata, MAI
  //                 una percentuale ferma.
  //   inviati/totale  byte, solo durante l'invio
  //   etaSec        stima dei secondi mancanti, solo quando calcolabile
  const [uploadStato, setUploadStato] = useState(null);

  // Ref so uploadFile/downloadFile always read current folder without stale closure
  const currentFolderRef = useRef(null);
  const [currentFolder, _setCurrentFolder] = useState(null);
  const [folderPath, setFolderPath]         = useState([]);

  // Keep a ref to the full file list so downloadFile can lookup encryptedName by id
  const filesRef = useRef([]);

  const [recentFiles, setRecentFiles]     = useState([]);
  const [recentLoading, setRecentLoading] = useState(false);
  const [preferiti, setPreferiti]             = useState([]);
  const [preferitiLoading, setPreferitiLoading] = useState(false);

  function setCurrentFolder(id) {
    currentFolderRef.current = id;
    _setCurrentFolder(id);
  }

  // ── resolve filename for display ─────────────────────────
  async function resolveFilename(f) {
    // Not encrypted — use plain name
    if (!f.encrypted) return f.plainName || f.storageFilename || 'file';
    if (!f.encryptedName) return f.plainName || '(unknown)';
    // Plain name stored as "plain:<b64>" — content encrypted, name readable
    if (f.encryptedName.startsWith('plain:')) {
      try { return decodeURIComponent(escape(atob(f.encryptedName.slice(6)))); } catch { return f.plainName || 'file'; }
    }
    // Legacy: fully encrypted name (old uploads) — try to decrypt
    try {
      const [nameIvB64, nameCipherB64] = f.encryptedName.split(':');
      if (!nameCipherB64) return f.plainName || '(encrypted)';
      const buf = await decryptBuffer(encKey, base64ToBuffer(nameCipherB64), nameIvB64);
      return new TextDecoder().decode(buf);
    } catch { return f.plainName || '(encrypted)'; }
  }

  // ── la didascalia ────────────────────────────────────────
  //
  // Stesso meccanismo del ramo vecchio dei nomi cifrati (qui sopra,
  // encryptBuffer/decryptBuffer con la chiave dell'utente): il server riceve
  // due stringhe base64 e non sa cosa contengono. L'iv che encryptBuffer
  // restituisce E' GIA' in base64: ri-codificarlo darebbe una stringa vuota.
  // SI DECIFRA UNA VOLTA SOLA (2026-09-18). La ricerca filtra anche sulle
  // didascalie, e il filtro gira a ogni carattere digitato: decifrare li'
  // dentro vorrebbe dire mille decifrature per ogni tasto. Qui c'e' una
  // memoria di sessione, in RAM e basta — mai su disco, mai in localStorage,
  // come per le miniature. La chiave della memoria e' id + iv: l'iv cambia a
  // ogni salvataggio della didascalia, quindi una didascalia modificata non
  // puo' restare quella vecchia, la voce vecchia semplicemente non si cerca
  // piu'. Si svuota uscendo dal vault, insieme alle miniature.
  async function risolviDidascalia(f) {
    if (!f.didascalia || !f.didascaliaIv) return '';
    if (!encKey) return '';
    const chiave = `${f.id}:${f.didascaliaIv}`;
    if (memoriaDidascalie.has(chiave)) return memoriaDidascalie.get(chiave);
    try {
      const buf = await decryptBuffer(encKey, base64ToBuffer(f.didascalia), f.didascaliaIv);
      const testo = new TextDecoder().decode(buf);
      memoriaDidascalie.set(chiave, testo);
      return testo;
    } catch { return ''; }
  }

  // Scrive, cambia o toglie la didascalia di un file. Testo vuoto = si toglie.
  const salvaDidascalia = useCallback(async (fileId, testo) => {
    const pulito = (testo || '').trim().slice(0, 200);
    let corpo = { didascalia: null, didascaliaIv: null };
    if (pulito) {
      if (!encKey) throw new Error('No encryption key — please log in again');
      const { ciphertext, iv } = await encryptBuffer(encKey, new TextEncoder().encode(pulito));
      corpo = { didascalia: bufferToBase64(ciphertext), didascaliaIv: iv };
    }
    const r = await apiSetDidascalia(fileId, corpo);
    // La didascalia appena scritta entra subito nella memoria di sessione:
    // cosi' la ricerca la trova senza doverla ridecifrare.
    if (r.didascaliaIv) memoriaDidascalie.set(`${fileId}:${r.didascaliaIv}`, pulito);
    const aggiorna = (lista) => lista.map((f) => (f.id === fileId
      ? { ...f, didascalia: r.didascalia, didascaliaIv: r.didascaliaIv, didascaliaTesto: pulito }
      : f));
    setFiles(aggiorna);
    setRecentFiles(aggiorna);
    return pulito;
  }, [encKey]);

  // ── load ─────────────────────────────────────────────────
  const loadFiles = useCallback(async (folderId) => {
    const folder = folderId !== undefined ? folderId : currentFolderRef.current;
    setLoading(true);
    try {
      const [serverFiles, serverFolders, q, allFoldersList] = await Promise.all([
        apiListFiles(folder),
        apiListFolders(folder),
        apiQuota(),
        apiListAllFolders()
      ]);
      setQuota(q);
      setFolders(serverFolders);
      setAllFolders(allFoldersList);
      const resolved = await Promise.all(
        serverFiles.map(async f => ({ ...f, filename: await resolveFilename(f), didascaliaTesto: await risolviDidascalia(f) }))
      );
      const sorted = resolved.sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt));
      setFiles(sorted);
      mergeIntoFilesRef(sorted);
    } finally { setLoading(false); }
  }, [encKey]); // eslint-disable-line

  // Recently uploaded files across all folders (used by the mobile "Recent" tab)
  const loadRecentFiles = useCallback(async () => {
    setRecentLoading(true);
    try {
      const serverFiles = await apiListRecent();
      const resolved = await Promise.all(
        serverFiles.map(async f => ({ ...f, filename: await resolveFilename(f), didascaliaTesto: await risolviDidascalia(f) }))
      );
      setRecentFiles(resolved); // already sorted desc by the API
      mergeIntoFilesRef(resolved);
    } finally { setRecentLoading(false); }
  }, [encKey]); // eslint-disable-line

  // L'ANTEPRIMA, DOPO IL CARICAMENTO E MAI PRIMA (2026-09-19).
  //
  // Si genera dal File che l'utente ha appena scelto — in chiaro, nelle mani
  // del browser — e si manda quando la riga esiste e ha un id. Tutto dentro
  // un try che non rilancia: se l'immagine e' corrotta, se il formato non si
  // sa disegnare, se la rete cade, il file resta caricato e le tre colonne
  // restano nulle. Un accessorio non rompe la funzione principale.
  //
  // `rimuoviSeFallisce` serve alla SOSTITUZIONE: li' una vecchia anteprima
  // c'e' gia', e tenerla vorrebbe dire mostrare il contenuto sbagliato.
  async function attaccaAnteprima(fileId, file, { rimuoviSeFallisce = false } = {}) {
    if (!fileId || !file) return null;
    try {
      const ant = await generaAnteprima(file, encKey);
      if (!ant) {
        if (rimuoviSeFallisce) { try { await apiTogliAnteprima(fileId); } catch { /* niente */ } }
        return null;
      }
      const r = await apiSetAnteprima(fileId, ant);
      const aggiorna = (lista) => lista.map((f) => (f.id === fileId
        ? { ...f, anteprimaNome: r.anteprimaNome, anteprimaIv: r.anteprimaIv, anteprimaV: r.anteprimaV }
        : f));
      setFiles(aggiorna); setRecentFiles(aggiorna);
      filesRef.current = aggiorna(filesRef.current);
      return r;
    } catch {
      if (rimuoviSeFallisce) { try { await apiTogliAnteprima(fileId); } catch { /* niente */ } }
      return null;
    }
  }

  // ── i preferiti ──────────────────────────────────────────
  // Stessa forma di loadRecentFiles: l'elenco arriva gia' ordinato dal
  // server (dal preferito piu' recente), i nomi e le didascalie si decifrano
  // qui come in ogni altro elenco.
  const loadPreferiti = useCallback(async () => {
    setPreferitiLoading(true);
    try {
      const serverFiles = await apiListPreferiti();
      const resolved = await Promise.all(
        serverFiles.map(async f => ({ ...f, filename: await resolveFilename(f), didascaliaTesto: await risolviDidascalia(f) }))
      );
      setPreferiti(resolved);
      mergeIntoFilesRef(resolved);
    } finally { setPreferitiLoading(false); }
  }, [encKey]); // eslint-disable-line

  // Mette o toglie il preferito. La data la decide il server (e' l'orologio
  // che ordina l'elenco); qui si aggiorna lo stato a mano, senza ricaricare
  // niente: un clic sulla stellina non deve far ridisegnare la cartella.
  const togglePreferito = useCallback(async (fileId, preferito) => {
    const r = await apiSetPreferito(fileId, preferito);
    const aggiorna = (lista) => lista.map((f) => (f.id === fileId ? { ...f, preferitoAt: r.preferitoAt } : f));
    setFiles(aggiorna);
    setRecentFiles(aggiorna);
    filesRef.current = aggiorna(filesRef.current);
    // Nell'elenco dei preferiti: chi entra si mette in cima (e' il piu'
    // recente), chi esce sparisce dalla lista.
    setPreferiti((lista) => {
      const senza = lista.filter((f) => f.id !== fileId);
      if (!r.preferitoAt) return senza;
      const gia = lista.find((f) => f.id === fileId);
      const riga = gia || filesRef.current.find((f) => f.id === fileId);
      return riga ? [{ ...riga, preferitoAt: r.preferitoAt }, ...senza] : senza;
    });
    return r.preferitoAt;
  }, []);

  function mergeIntoFilesRef(newFiles) {
    const map = new Map(filesRef.current.map(f => [f.id, f]));
    newFiles.forEach(f => map.set(f.id, f));
    filesRef.current = Array.from(map.values());
  }

  // ── navigate ─────────────────────────────────────────────
  // Every folder-level navigation pushes its own history entry (see the
  // popstate listener below) so the browser back button un-nests one folder
  // at a time instead of leaving the app — previously nothing here ever
  // touched history at all. { page: 'vault' } keeps App.js's own router
  // happy (it still just renders VaultPage); folderId is what this hook's
  // own listener reacts to. App.js's buffer-reseed effect specifically
  // skips re-seeding when folderId is set, so it doesn't bury these entries.
  const folderUrl = (folderId) => folderId ? `/vault/${folderId}` : '/';

  const openFolder = useCallback((folder) => {
    setCurrentFolder(folder.id);
    setFolderPath(prev => [...prev, { id: folder.id, name: folder.name }]);
    window.history.pushState({ page: 'vault', folderId: folder.id }, '', folderUrl(folder.id));
    setTimeout(() => loadFiles(folder.id), 0);
  }, [loadFiles]);

  const navigateTo = useCallback((folderId, idx, opts = {}) => {
    const { pushHistory = true } = opts;
    if (folderId === null) {
      setCurrentFolder(null);
      setFolderPath([]);
      if (pushHistory) window.history.pushState({ page: 'vault', folderId: undefined }, '', '/');
      loadFiles(null);
    } else {
      setCurrentFolder(folderId);
      setFolderPath(prev => prev.slice(0, idx + 1));
      if (pushHistory) window.history.pushState({ page: 'vault', folderId }, '', folderUrl(folderId));
      loadFiles(folderId);
    }
  }, [loadFiles]);

  // Jump straight to any folder (e.g. from a search result, a deep link, or
  // this hook's own popstate handler) — unlike navigateTo, this doesn't
  // assume the target is already part of the current breadcrumb; it
  // rebuilds the full ancestor chain from allFolders. pushHistory:false is
  // for callers reacting to a browser back/forward that already moved the
  // history pointer — pushing again there would double up entries.
  const navigateToFolderId = useCallback((folderId, opts = {}) => {
    const { pushHistory = true } = opts;
    if (!folderId) { navigateTo(null, -1, opts); return; }
    const path = [];
    let cur = allFolders.find(f => f.id === folderId);
    while (cur) { path.unshift({ id: cur.id, name: cur.name }); cur = allFolders.find(f => f.id === cur.parentId); }
    setCurrentFolder(folderId);
    setFolderPath(path);
    if (pushHistory) window.history.pushState({ page: 'vault', folderId }, '', folderUrl(folderId));
    loadFiles(folderId);
  }, [allFolders, loadFiles]); // eslint-disable-line

  // Reacts to the browser back/forward buttons moving through the folder-
  // level entries pushed above — never pushes itself (the history pointer
  // already moved). Ignores popstate events that aren't "ours" (e.g. App.js
  // navigating between top-level pages), identified by state.page==='vault'.
  useEffect(() => {
    function onPopState(e) {
      if (!e.state || e.state.page !== 'vault') return;
      const folderId = e.state.folderId || null;
      if (folderId) navigateToFolderId(folderId, { pushHistory: false });
      else navigateTo(null, -1, { pushHistory: false });
    }
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [navigateToFolderId, navigateTo]);

  // ── search — fetches every file/folder the user owns and resolves filenames
  // client-side (server never sees plaintext names), for the Dock's search flyout.
  const searchAllFiles = useCallback(async () => {
    const [serverFiles, serverFolders] = await Promise.all([apiListAllFiles(), apiListAllFolders()]);
    const resolved = await Promise.all(
      serverFiles.map(async f => ({ ...f, filename: await resolveFilename(f), didascaliaTesto: await risolviDidascalia(f) }))
    );
    return { files: resolved, folders: serverFolders };
  }, [encKey]); // eslint-disable-line

  // ── folders CRUD ──────────────────────────────────────────
  const createFolder = useCallback(async (name, parentId) => {
    // Use explicit parentId if provided, otherwise current folder
    const targetParent = parentId !== undefined ? parentId : currentFolderRef.current;
    const folder = await apiCreateFolder(name, targetParent);
    // Only add to visible list if created in current folder
    if (targetParent === currentFolderRef.current) {
      setFolders(prev => [...prev, folder]);
    }
    return folder;
  }, []);

  // NIENTE CARTELLE PREDEFINITE (2026-09-05). Prima ogni nuovo account
  // nasceva con Documents/Images/Important/Archive: quattro cartelle vuote,
  // in inglese, su un sito italiano, davanti a chi non ha ancora capito cosa
  // deve fare. Un vault vuoto con un'istruzione sola e' piu' chiaro (vedi lo
  // stato vuoto in VaultPage). Gli account ESISTENTI non si toccano: le loro
  // cartelle restano dove sono, questa funzione semplicemente non ne crea
  // piu' di nuove. Resta chiamabile (VaultPage la invoca al montaggio) e non
  // fa nulla, cosi' non serve toccare il chiamante.
  // ── IL PUNTO UNICO CHE RILEGGE LO STATO DELL'ACCOUNT (2026-09-13) ──────
  //
  // Spazio usato e numero di file cambiano a ogni operazione, e da quei due
  // numeri dipendono il banner rosso, la pastiglia in alto e il riquadro del
  // muro. Cancellando dei file il banner restava li' a dire che eri al muro
  // finche' non ricaricavi la pagina: lo stato non veniva riletto.
  //
  // Una funzione sola, chiamata da TUTTE le operazioni che spostano quei
  // numeri — caricamento, cancellazione, spostamento, cancellazione di una
  // cartella — invece di una chiamata sparsa in ognuna. Sparsa vuol dire che
  // la prossima operazione che qualcuno aggiunge se ne dimentica.
  //
  // Serve anche a chi carica mentre naviga altrove: la cartella sbagliata non
  // si ricarica, ma lo stato in cima si aggiorna lo stesso.
  // Restituisce anche quello che ha letto: chi deve DECIDERE sulla base dello
  // stato nuovo (il riquadro al risveglio dell'app) non puo' aspettare che
  // React abbia fatto il giro, e non deve leggere lo stato vecchio.
  const rileggiStato = useCallback(async () => {
    try { const q = await apiQuota(); setQuota(q); return q; } catch { return null; }
  }, []);
  // Il nome vecchio resta come sinonimo: era usato in tre punti e cambiarlo
  // tutto adesso aggiungerebbe rumore a un cambio che deve restare leggibile.
  const loadQuotaSoltanto = rileggiStato;

  const initDefaultFolders = useCallback(async () => {}, []);

  const moveFile = useCallback(async (fileId, targetFolderId) => {
    await apiMoveFile(fileId, targetFolderId);
    await loadFiles(currentFolderRef.current);
  }, [loadFiles]);   // loadFiles rilegge gia' la quota

  const deleteFolder = useCallback(async (folderId) => {
    await apiDeleteFolder(folderId);
    setFolders(prev => prev.filter(f => f.id !== folderId));
    // Una cartella cancellata porta via i suoi file: e' l'operazione che puo'
    // liberare di piu', ed e' proprio quella dopo cui il banner deve sparire.
    await rileggiStato();
  }, [rileggiStato]);

  const renameFolder = useCallback(async (folderId, name) => {
    const updated = await apiRenameFolder(folderId, name);
    setFolders(prev => prev.map(f => f.id === folderId ? { ...f, name: updated.name } : f));
  }, []);

  // Sposta una cartella con tutto quello che contiene. Non si tocca niente
  // sul disco: cambia solo di chi e' figlia. Dopo si ricarica la vista
  // corrente, perche' la cartella puo' essere appena uscita da qui.
  const moveFolder = useCallback(async (folderId, parentId) => {
    await apiMoveFolder(folderId, parentId);
    await loadFiles(currentFolderRef.current);
  }, [loadFiles]);


  // ── upload ────────────────────────────────────────────────
  const uploadFile = useCallback(async (file, opts = {}) => {
    const {
      encryptContent = true, encryptName = true, targetFolderId, ripresa = null,
      // La coda passa questi due (2026-09-13). `avanzamento` riceve lo stato di
      // QUESTO file: prima esisteva un solo indicatore globale, e con piu' file
      // in fila l'ultimo cancellava il penultimo. `segnale` e' l'AbortSignal
      // della riga, per l'Annulla.
      avanzamento: avanzamentoEsterno = null, segnale = null, onSessione = null,
    } = opts;
    const folderId = targetFolderId !== undefined ? targetFolderId : currentFolderRef.current;
    setUploading(true);
    // Quando la coda ascolta, l'indicatore globale non serve piu': si scrive
    // dove guarda la riga. Resta per chi chiama uploadFile da fuori dalla coda
    // (sostituzione di un file, caricamento di una cartella).
    const avanti = avanzamentoEsterno
      ? avanzamentoUnico(avanzamentoEsterno.stato, avanzamentoEsterno.percento)
      : avanzamentoUnico(setUploadStato, setUploadProgress);

    // Ricaricare l'elenco SOLO se sei ancora dove il file sta arrivando.
    // Prima si ricaricava sempre, e chi navigava mentre caricava si ritrovava
    // riportato nella cartella di destinazione a ogni file finito: e' quello
    // che faceva sembrare il vault bloccato.
    const ricaricaSeSeiAncoraLi = async () => {
      if (currentFolderRef.current === folderId) await loadFiles(folderId);
      else await loadQuotaSoltanto();
    };

    // ── LA STRADA A BLOCCHI ──────────────────────────────────────────────
    // Si prende solo se il server ha le rotte accese e se il contenuto va
    // cifrato: il nome in chiaro resta in chiaro come oggi. Per tutto il
    // resto — file non cifrati, nome cifrato, interruttore spento — vale
    // parola per parola il codice di prima, che non e' stato toccato.
    //
    // Si ASPETTA la risposta sull'interruttore invece di leggere lo stato:
    // la domanda al server parte all'apertura del vault, e chi trascina un
    // file nel primo secondo la troverebbe ancora senza risposta. Nella prova
    // del 13/09 e' successo davvero — il file e' partito sulla strada vecchia
    // e la ripresa non gli e' stata offerta. blocchiDisponibili() e' una
    // promessa sola, chiesta una volta per sessione: aspettarla non costa un
    // secondo giro.
    const aBlocchi = encryptContent && !encryptName && await blocchiDisponibili();
    if (aBlocchi) {
      // Su una ripresa NON si riparte da zero: la riga sa gia' a che punto
      // era, e riscriverle 0% la farebbe sembrare ricominciata da capo.
      if (!ripresa) avanti.blocchi(0, 1);
      try {
        // Dentro il try: altrimenti un lancio qui lascerebbe l'indicatore
        // acceso per sempre, perche' setUploading(true) e' gia' passato.
        if (!encKey) throw new Error('No encryption key — please log in again');
        const riga = await caricaABlocchi(file, encKey, {
          folderId, ripresa, segnale, onSessione,
          avanzamento: ({ fatti, totale, fase, posizione }) => {
            if (fase && avanzamentoEsterno?.fase) avanzamentoEsterno.fase(fase, posizione);
            avanti.blocchi(fatti, totale);
            // In BYTE, per chi misura la velocita': la percentuale arrotondata
            // su un file da 4 GB si muove a scatti da 40 MB, e una finestra di
            // cinque secondi che vede scatti simili da' numeri assurdi.
            if (avanzamentoEsterno?.byte) {
              avanzamentoEsterno.byte(Math.min(file.size, fatti * DIMENSIONE_BLOCCO), file.size);
            }
          },
        });
        avanti.fine();
        // Prima l'anteprima, poi il ricaricamento della cartella: cosi' la
        // griglia si ridisegna gia' con le tre colonne piene invece di
        // mostrare il file senza e correggersi un attimo dopo.
        if (puoAvereAnteprima(file)) await attaccaAnteprima(riga?.id, file);
        await ricaricaSeSeiAncoraLi();
        return riga;
      } finally {
        setUploading(false);
        if (!avanzamentoEsterno) setTimeout(() => { setUploadProgress(0); setUploadStato(null); }, 600);
      }
    }

    avanti.cifratura(0);
    try {
      let payload;
      if (encryptContent) {
        if (!encKey) throw new Error('No encryption key — please log in again');
        const { blob, iv, encryptedName, size } = await encryptFile(encKey, file, encryptName, ({ frazione }) => avanti.cifratura(frazione));
        payload = { blob, iv, encryptedName, size, mimeType: file.type, encrypted: true, folderId };
      } else {
        payload = {
          blob: new Blob([await leggiFileIntero(file, (f) => avanti.cifratura(f))], { type: file.type }),
          encrypted: false, size: file.size, mimeType: file.type,
          plainName: file.name, folderId
        };
      }
      avanti.invio(0, null);
      const result = await apiUploadFile(payload, ({ inviati, totale }) => {
        avanti.invio(inviati, totale);
        if (avanzamentoEsterno?.byte) avanzamentoEsterno.byte(inviati, totale || file.size);
      }, segnale);
      avanti.fine();
      if (puoAvereAnteprima(file)) await attaccaAnteprima(result?.id, file);
      await ricaricaSeSeiAncoraLi();
      return result;
    } finally {
      setUploading(false);
      if (!avanzamentoEsterno) setTimeout(() => { setUploadProgress(0); setUploadStato(null); }, 600);
    }
  }, [encKey, loadFiles, loadQuotaSoltanto]);

  // ── replace content of an existing file (archives old content as a version) ──
  const replaceFile = useCallback(async (fileId, file, opts = {}) => {
    const { encryptContent = true, encryptName = false } = opts;
    setUploading(true);
    const avanti = avanzamentoUnico(setUploadStato, setUploadProgress);
    avanti.cifratura(0);
    try {
      let payload;
      if (encryptContent) {
        if (!encKey) throw new Error('No encryption key — please log in again');
        const { blob, iv, encryptedName, size } = await encryptFile(encKey, file, encryptName, ({ frazione }) => avanti.cifratura(frazione));
        payload = { blob, iv, encryptedName, size, mimeType: file.type, encrypted: true };
      } else {
        payload = {
          blob: new Blob([await leggiFileIntero(file, (f) => avanti.cifratura(f))], { type: file.type }),
          encrypted: false, size: file.size, mimeType: file.type, plainName: file.name
        };
      }
      avanti.invio(0, null);
      const result = await apiReplaceFile(fileId, payload, ({ inviati, totale }) => avanti.invio(inviati, totale));
      // SOSTITUZIONE: l'anteprima si RIGENERA qui, dove il browser ha il file
      // NUOVO in chiaro. Se non riesce, le tre colonne si azzerano invece di
      // restare com'erano: meglio nessuna anteprima che l'anteprima del
      // contenuto vecchio.
      await attaccaAnteprima(fileId, file, { rimuoviSeFallisce: true });
      avanti.fine();
      await loadFiles(currentFolderRef.current);
      return result;
    } finally {
      setUploading(false);
      setTimeout(() => { setUploadProgress(0); setUploadStato(null); }, 600);
    }
  }, [encKey, loadFiles]);

  // ── download — uses local filesRef to get encryptedName ──
  // `opts.avanzamento({scaricati, totale})` e `opts.segnale` arrivano dal
  // pannello Trasferimenti: prima lo scaricamento era cieco e non annullabile.
  const downloadFile = useCallback(async (fileId, displayFilename, opts = {}) => {
    const { avanzamento = null, segnale = null } = opts;
    // Find the file record we already have in memory (has encryptedName + iv intact)
    const fileRecord = filesRef.current.find(f => f.id === fileId);

    // FORMATO A BLOCCHI. Si riconosce dalla riga, mai dai primi byte del file:
    // "LCB2" sono quattro byte e un file vecchio puo' cominciare cosi' per
    // caso. Questo ramo legge a flusso e non tiene mai in memoria piu' di un
    // blocco — e' la meta' che rende utile l'altra.
    if (fileRecord && Number(fileRecord.formatVersion) === 2) {
      if (!encKey) throw new Error('No encryption key — please log in again');
      const nome = fileRecord.plainName || displayFilename || 'file';
      await scaricaABlocchi(fileId, nome, encKey, avanzamento, segnale);
      return;
    }

    // LO SCARICAMENTO DEL FORMATO VECCHIO NON SI PUO' SEGUIRE (2026-09-13).
    //
    // apiDownloadFile resta com'era: `await res.blob()`, un passo atomico. Per
    // vederne l'avanzamento servirebbe leggere il corpo a flusso, e quella e'
    // la strada da cui scaricano TUTTI — compresi i file dei clienti. Il
    // cambio era pronto e provato a meta': le prove sono cadute per difetti
    // del banco, non del prodotto, e una barra di avanzamento non vale il
    // rischio di rompere l'unica cosa che oggi funziona bene.
    //
    // Si avvisa la riga che la percentuale non si sapra': meglio "in corso"
    // che uno zero fermo che sembra un blocco.
    if (avanzamento) avanzamento({ scaricati: null, totale: null });
    const { blob, isEncrypted } = await apiDownloadFile(fileId, marcaVersione(fileRecord));

    if (!isEncrypted) {
      // Plain file — download directly with real filename
      const finalName = fileRecord?.plainName || displayFilename || 'file';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = finalName;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      return;
    }

    // Encrypted file
    if (!encKey) throw new Error('No encryption key — please log in again');
    if (!fileRecord) throw new Error('File record not found — try refreshing the page');

    let finalBlob, finalName;

    try {
      // Try to decrypt name from encryptedName field
      const result = await decryptFile(
        encKey,
        blob,
        fileRecord.iv,
        fileRecord.encryptedName
      );
      finalBlob = result.plainBlob;
      // Use decrypted filename — fall back to displayFilename from UI if decryption gave generic name
      finalName = (result.filename && result.filename !== 'decrypted_file')
        ? result.filename
        : (displayFilename || fileRecord.filename || 'decrypted_file');
    } catch (err) {
      throw new Error('Decryption failed: ' + err.message);
    }

    // Ensure filename has correct extension from display name if decrypt returned generic
    if (finalName === 'decrypted_file' && displayFilename && displayFilename !== '(encrypted)') {
      finalName = displayFilename;
    }

    const url = URL.createObjectURL(finalBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = finalName;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }, [encKey]);

  // ── l'interruttore, chiesto una volta sola per sessione ──
  // La domanda parte qui, all'apertura, cosi' quando servira' la risposta c'e'
  // gia'. Ma chi la usa la ASPETTA (vedi uploadFile): non esiste una finestra
  // in cui il vault crede che l'interruttore sia spento solo perche' e'
  // presto.
  useEffect(() => {
    if (!encKey) return;
    blocchiDisponibili();
    // Si spazza lo spazio privato del browser: se uno scaricamento e' finito
    // male, il suo file di lavoro non deve restare sul disco dell'utente.
    //
    // E si RIPASSA ogni minuto, non solo all'apertura. Una passata sola non
    // basta ed e' verificato: chi chiude il browser appena finito di scaricare
    // lascia un file di lavoro che alla riapertura batte ancora un colpo
    // recente — quindi lo spazzino lo risparmia, giustamente — e poi non passa
    // piu' nessuno. Con la ripassata il colpo invecchia, e al giro dopo il
    // file se ne va.
    spazzaFileDiLavoro();
    const ogniTanto = setInterval(() => { spazzaFileDiLavoro(); }, 60 * 1000);
    return () => clearInterval(ogniTanto);
  }, [encKey]);

  // Le sessioni lasciate a meta'. Non si riprende mai da soli: questo elenco
  // serve a CHIEDERE, perche' due file diversi possono avere lo stesso nome e
  // la stessa dimensione.
  const elencaSessioniAperte = useCallback(async () => {
    // Stessa ragione: si aspetta la risposta, non si legge uno stato che
    // potrebbe non essere ancora arrivato.
    if (!(await blocchiDisponibili())) return [];
    try { return await sessioniAperte(); } catch { return []; }
  }, []);

  // ── delete ────────────────────────────────────────────────
  const deleteFile = useCallback(async (fileId) => {
    await apiDeleteFile(fileId);
    setFiles(prev => prev.filter(f => f.id !== fileId));
    setRecentFiles(prev => prev.filter(f => f.id !== fileId));
    filesRef.current = filesRef.current.filter(f => f.id !== fileId);
    await rileggiStato();
  }, [rileggiStato]);

  return {
    files, folders, allFolders, quota, loading, uploading, uploadProgress, uploadStato,
    rileggiStato,
    currentFolder, folderPath,
    recentFiles, recentLoading, loadRecentFiles,
    preferiti, preferitiLoading, loadPreferiti, togglePreferito,
    loadFiles, openFolder, navigateTo, navigateToFolderId, searchAllFiles,
    createFolder, deleteFolder, renameFolder, moveFolder,
    initDefaultFolders, moveFile,
    uploadFile, replaceFile, downloadFile, deleteFile, salvaDidascalia,
    elencaSessioniAperte
  };
}
